import _ from 'lodash';
import { ChangeEvent, Dispatch } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FlowItem, StepItem } from '../../../../_components/flow-builder/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FLOW_CONFIG, initialState, journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { getAllServices } from '../../../../store/_api';
import { IngressEvent, Service, ServiceOperation } from '../../../../store/_api/TypeDefs';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { FlowItemConditions, PayloadTransformation, SidebarState } from '../TypeDefs';
import { translateLabel } from './PrepareJourneyHelpers';

/**
 * @method findEmptyXOnCanvas
 * @description Finds empty X position on canvas
 * @param items FlowItems
 * @param itemWidth item width
 * @param itemHeight item height
 * @param  x canvas x position
 * @param  y canvas y position
 * @returns new X position
 */
export const findEmptyXOnCanvas = ({
  items,
  itemWidth,
  itemHeight,
  x,
  y
}: {
  items: FlowItem[] | StepItem[],
  itemWidth: number,
  itemHeight: number,
  x: number,
  y: number
}) => {
  const bottom = y + itemHeight;
  let newX = x;

  // find the positions which are overlapping with current y
  const existingPos = items.map((i) => ({
    l: i.x,
    t: i.y,
    r: i.x + itemWidth,
    b: i.y + itemHeight
  })).filter((p) => ((y >= p.t && y <= p.b) || (bottom >= p.t && bottom <= p.b)));
  existingPos.sort((i1, i2) => (i1.l - i2.l));
  existingPos.forEach((pos) => {
    const right = newX + itemWidth;
    if ((newX >= pos.l && newX <= pos.r) || (right >= pos.l && right <= pos.r)) {
      newX = pos.r + journeyCanvasConstants.itemHorizontalGap;
    }
  });

  return newX;
};

/**
 * @method findEmptyYOnCanvas
 * @description Finds empty Y position on canvas
 * @param items FlowItems
 * @param itemWidth item width
 * @param itemHeight item height
 * @param  x canvas x position
 * @param  y canvas y position
 * @returns new Y position
 */
export const findEmptyYOnCanvas = ({
  items,
  itemWidth,
  itemHeight,
  x,
  y
}: {
  items: FlowItem[] | StepItem[],
  itemWidth: number,
  itemHeight: number,
  x: number,
  y: number
}) => {
  const right = x + itemWidth;
  let newY = y;

  // find the positions which are overlapping with current x
  const existingPos = items.map((i) => ({
    l: i.x,
    t: i.y,
    r: i.x + itemWidth,
    b: i.y + itemHeight
  })).filter((p) => ((x >= p.l && x <= p.r) || (right >= p.l && right <= p.r)));
  existingPos.sort((i1, i2) => (i1.t - i2.t));
  existingPos.forEach((pos) => {
    const bottom = newY + itemHeight;
    if ((newY >= pos.t && newY <= pos.b) || (bottom >= pos.t && bottom <= pos.b)) {
      newY = pos.b + journeyCanvasConstants.itemVerticalGap;
    }
  });

  return newY;
};

/**
 * @method findEmptyPositionOnCanvas
 * @description Find empty X, Y position for new item on canvas based on prev item
 * @param items Current Flow Items
 * @param isExernal is the element external
 * @param prevItem After which the new item needs to be created
 * @param orientation Horizontal orientation
 * @returns Empty X, Y position on canvas
 */
export const findEmptyPositionOnCanvas = (items: FlowItem[] | StepItem[], isExternal: boolean, prevItem?: FlowItem | StepItem, designingStep?: boolean, orientation: Orientation = Orientation.HORIZONTAL): { x: number, y: number } => {
  const { itemWidth = 0, itemHeight = 0, stepCardHeight = 0, stepCardWidth = 0 } = FLOW_CONFIG;

  if (!prevItem) {
    return isExternal ? journeyCanvasConstants.rootExternalItemPosition : journeyCanvasConstants.rootItemPosition;
  }
  if (orientation === Orientation.VERTICAL) {
    let { x, y } = prevItem;
    y += designingStep ? itemHeight + journeyCanvasConstants.itemVerticalGap : stepCardHeight + journeyCanvasConstants.itemVerticalGap;
    x = findEmptyXOnCanvas({ items, itemWidth, itemHeight, x, y });
    return { x, y };
  }
  let { x, y } = prevItem;
  x += designingStep ? itemWidth + journeyCanvasConstants.itemHorizontalGap : stepCardWidth + journeyCanvasConstants.itemHorizontalGap;
  y = findEmptyYOnCanvas({ items, itemWidth, itemHeight, x, y });
  return { x, y };
};

/**
 * @method generateNodeId
 * @description generates UUID starting with NID-
 */
export const generateNodeId = () => `NID-${uuidv4().toUpperCase()}`;

/** To convert array-Buffer to string */
export const arrayBufferToString = (exportedPrivateKey: unknown) => {
  const ePrivateKey = exportedPrivateKey as ArrayBuffer;
  const byteArray = new Uint8Array(ePrivateKey);
  let byteString = '';
  byteArray.forEach((data) => {
    byteString += String.fromCodePoint(data);
  });
  return byteString;
};

/**
 * @method getMepValue
 * @description set map to SYNC if node is external
 * @param isExternal check if the flowItem is external
 * @returns set map to SYNC
 */
export const getMepValue = (isExternal: boolean) => (isExternal ? translateLabel('labels.sync') : undefined);

/**
 * @method filterPayloadTransformationForConvergence
 * @description Filters an array of PayloadTransformation objects based on the ingress and egress arrays.
 * @param {PayloadTransformation[]} payloadTransformation - An array of PayloadTransformation objects.
 * @param {string[]} ingress - An array of strings representing the ingress values to filter by.
 * @param {string[]} egress - An array of strings representing the egress values to filter by.
 * @param {string} [ingressOp] - A string representing the ingress operation to perform. If set to 'FlowItemConditions.AND', the function will filter the payloadTransformation array based on both the ingress and egress arrays.
 * @returns {PayloadTransformation[]|undefined} - The filtered array of PayloadTransformation objects, or undefined if ingressOp is not 'FlowItemConditions.AND'.
 */
export const filterPayloadTransformationForConvergence = (payloadTransformation: PayloadTransformation[], ingress: string[], egress: string[], ingressOp?: string) => {
  if (ingressOp === FlowItemConditions.AND || ingressOp === FlowItemConditions.T_AND) {
    const pts = payloadTransformation.filter((pt) => ingress.includes(pt.ingress) && egress.includes(pt.egress));
    return pts;
  }
  return undefined;
};

/**
 * @method calculateDuration
 * @description Calculates the duration in hours, minutes, and seconds based on the maximum execution time.
 * @param {number} maxExecTime - The maximum execution time in seconds.
 * @returns {{ hours: number, minutes: number, seconds: number }} - An object containing the calculated duration in hours, minutes, and seconds.
 */
export const calculateDuration = (maxExecTime: number) => {
  let maxExec = maxExecTime;
  const hrs = Math.floor(maxExec / 3600);
  maxExec %= 3600;
  const mins = Math.floor(maxExec / 60);
  const secs = maxExec % 60;
  return {
    hours: hrs,
    minutes: mins,
    seconds: secs
  };
};

/**
 * @method hideSidebar
 * @description hide sidebar when clicked on canvas
 */
export const hideSidebar = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.HIDE_SIDEBAR });
};

/**
 * @method startBtnClickHandler
 * @description function for start button on canvas
 */
export const startBtnClickHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.SELECT_START });
};

const debounceDelay = 300;
/**
 * @method serviceFilterWithDebounce
 * @description handles delay after the filtered journey names
 */
export const serviceFilterWithDebounce = _.debounce((value: string, dispatch: Dispatch<UpdateActions>, sidebarState: SidebarState) => {
  if (sidebarState === SidebarState.ALL_SERVICES) {
    if (value) {
      if (/^[a-zA-Z0-9-]*$/.test(value)) {
        dispatch({ type: Actions.SIDEBAR_DATA_LOADING });
        (async () => {
          const services: Service[] | undefined = await getAllServices(initialState.allServicesDataLoad.page, journeyCanvasConstants.allServicesPageSize, value);
          if (services) {
            dispatch({ type: Actions.SEARCH_ITEMS, payload: { services } });
          } else {
            dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
          }
        })();
      } else {
        dispatch({ type: Actions.SET_SIDEBAR_INVALID_SEARCH });
      }
    } else {
      // search cleared
      dispatch({ type: Actions.SEARCH_ITEMS, payload: { services: [] } });
    }
  }
}, debounceDelay);

/**
 * @method serviceFilterHandler
 * @description search the items from the search bar
 * @param {ChangeEvent<HTMLInputElement>} e
 */
export const serviceFilterHandler = (e: ChangeEvent<HTMLInputElement>, dispatch: Dispatch<UpdateActions>, sidebarState: SidebarState) => {
  const data = e.target.value;
  dispatch({ type: Actions.SET_SEARCH_DATA, payload: { data } });
  serviceFilterWithDebounce(data, dispatch, sidebarState);
};

/**
 * @method ingressEventClickHandler
 * @description selecting an ingress event from associated list of events
 * @param serviceOps
 */
export const ingressEventClickHandler = (serviceOps: ServiceOperation, ingressEvent: IngressEvent, dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.PICK_INGRESS_EVENT, payload: { serviceOps, ingressEvent } });
};

/**
 * @method fullScreenHandler
 * @description Toggles the fullscreen mode of the document.
 * @returns {void}
 */
export const fullScreenHandler = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else if (document.exitFullscreen) {
    document.exitFullscreen();
  }
};

/**
 * @method conditionSelectHandler
 * @description Dispatches condition change to the specified value
 * @param nodeId The node whose condition is being changed
 * @param dispatch Reducer dispatch
 * @param flowItems Current flow items
 * @param conditionText The condition to set (OR, AND, T-AND)
 */
export const conditionSelectHandler = (nodeId: string, dispatch: Dispatch<UpdateActions>, flowItems: FlowItem[], conditionText: string) => {
  const node = flowItems.find((fi) => fi.nodeId === nodeId);
  if (node) {
    console.log('[ConditionDispatch] nodeId:', nodeId, '| conditionText dispatched:', conditionText);
    dispatch({
      type: Actions.CHANGE_CONDITION_TEXT,
      payload: { nodeId, conditionText }
    });
  }
};

/**
 * @method onPayloadSaveHandler
 * @description Handles the payload save event by dispatching an action to update the payload for converging services.
 * @param {PayloadTransformation[]} localPayloadTransformation - An array of PayloadTransformation objects representing the local payload transformations.
 * @param {Dispatch<UpdateActions>} dispatch - A dispatch function from the Redux store to dispatch the update action.
 * @returns {void}
 */
export const onPayloadSaveHandler = (nodeId: string, localPayloadTransformation: PayloadTransformation[], dispatch: Dispatch<UpdateActions>, flowItems: FlowItem[]) => {
  const node = flowItems.find((fi) => fi.nodeId === nodeId);
  if (node) {
    dispatch({
      type: Actions.SET_PAYLOAD_FOR_CONVERGING_SERVICES,
      payload: {
        nodeId,
        payloadData: localPayloadTransformation
      }
    });
  }
};

/**
 * @method closeTransformPayloadModal
 * @description Closes the transform payload modal by dispatching an action to close the modal.
 * @param {Dispatch<UpdateActions>} dispatch - A dispatch function from the Redux store to dispatch the update action.
 * @returns {void}
 */
export const closeTransformPayloadModal = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({
    type: Actions.CLOSE_PAYLOAD_TRANSFORM_MODAL
  });
};

/**
 * @method setOpenSchedulerModal
 * @description Sets the open state of the scheduler modal by dispatching an action to open the modal.
 * @param {Dispatch<UpdateActions>} dispatch - A dispatch function from the Redux store to dispatch the update action.
 * @returns {void}
 */
export const setOpenSchedulerModal = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.OPEN_SCHEDULER_MODAL });
};

/**
 * @method setCloseSchedulerModal
 * @description Sets the close state of the scheduler modal by dispatching an action to close the modal.
 * @param {Dispatch<UpdateActions>} dispatch - A dispatch function from the Redux store to dispatch the update action.
 * @returns {void}
 */
export const setCloseSchedulerModal = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.CLOSE_SCHEDULER_MODAL });
};

/**
 * @method getSearchParamOrDefault
 * @description Get search param value or default value
 * @param searchParams
 * @param key
 * @param defaultValue
 * @returns search param value or default value
 */
export const getSearchParamOrDefault = (searchParams: URLSearchParams, key: string, defaultValue = '') => searchParams.get(key) ?? defaultValue;
